import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { RequestError } from '@/services/config';
import {
    useSiteFishGroupActionsController_operate,
    useSiteFishGroupActionsController_recordDelousing,
    useSiteFishGroupActionsController_relocate,
} from '@/services/hooks';

import { invalidateFishGroupQueries } from './_invalidateQueries';
import {
    mapSiteDelousingToRequest,
    mapSiteMergeToRequest,
    mapSiteMoveToRequest,
    mapSiteSplitToRequest,
    operationGroupIds,
    type SiteDelousingInput,
    type SiteMergeInput,
    type SiteMoveInput,
    type SiteSplitInput,
} from './siteActions.mappers';

interface SiteActionState {
    isLoading: boolean;
    error: Error | RequestError | null;
    reset: () => void;
}

/**
 * A customer's writes from the site board, on the location-scoped routes that admit site
 * managers. Each resolves once the board's queries have refetched, and rejects with the
 * request error.
 */
export interface UseSiteFishGroupActionsResult {
    move: (input: SiteMoveInput) => Promise<void>;
    recordDelousing: (input: SiteDelousingInput) => Promise<void>;
    split: (input: SiteSplitInput) => Promise<void>;
    merge: (input: SiteMergeInput) => Promise<void>;
    state: SiteActionState;
}

export const useSiteFishGroupActions = (
    clientId: number,
    locationId: number
): UseSiteFishGroupActionsResult => {
    const queryClient = useQueryClient();
    const relocate = useSiteFishGroupActionsController_relocate();
    const delousing = useSiteFishGroupActionsController_recordDelousing();
    const operate = useSiteFishGroupActionsController_operate();
    // Covers this site's board too, with the client's other fish group reads, and each changed
    // group's own detail, events, stays and lineage, which internal pages don't refetch on mount.
    const refetch = (fishGroupIds: readonly number[]) =>
        Promise.all(
            fishGroupIds.map((fishGroupId) =>
                invalidateFishGroupQueries(queryClient, clientId, fishGroupId)
            )
        );
    // Busy from the click until the board has refetched, not only while the request runs:
    // a dialog's Confirm must not come back while the refresh still runs, or it saves twice.
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const exclusive =
        <TInput>(action: (input: TInput) => Promise<void>) =>
        async (input: TInput): Promise<void> => {
            if (inFlight.current) throw new Error('A fish group action is already running');
            inFlight.current = true;
            setBusy(true);
            try {
                await action(input);
            } finally {
                inFlight.current = false;
                setBusy(false);
            }
        };

    const move = exclusive(async (input: SiteMoveInput) => {
        await relocate.mutateAsync({
            clientId,
            locationId,
            fishGroupId: input.fishGroupId,
            requestBody: mapSiteMoveToRequest(input),
        });
        await refetch([input.fishGroupId]);
    });
    const recordDelousing = exclusive(async (input: SiteDelousingInput) => {
        await delousing.mutateAsync({
            clientId,
            locationId,
            fishGroupId: input.fishGroupId,
            requestBody: mapSiteDelousingToRequest(input),
        });
        await refetch([input.fishGroupId]);
    });
    const split = exclusive(async (input: SiteSplitInput) => {
        const response = await operate.mutateAsync({
            clientId,
            locationId,
            requestBody: mapSiteSplitToRequest(input),
        });
        await refetch(operationGroupIds(response.data, [input.sourceId]));
    });
    const merge = exclusive(async (input: SiteMergeInput) => {
        const response = await operate.mutateAsync({
            clientId,
            locationId,
            requestBody: mapSiteMergeToRequest(input),
        });
        await refetch(
            operationGroupIds(
                response.data,
                input.sources.map((source) => source.fishGroupId)
            )
        );
    });

    return {
        move,
        recordDelousing,
        split,
        merge,
        state: {
            isLoading: busy || relocate.isPending || delousing.isPending || operate.isPending,
            error: relocate.error ?? delousing.error ?? operate.error,
            reset: () => {
                relocate.reset();
                delousing.reset();
                operate.reset();
            },
        },
    };
};
